/**
 * Curated prompt bank for the hero and the composer typeahead. Static and
 * client-side on purpose: zero latency, and each entry teaches a capability
 * the user may not know they can ask for.
 *
 * Two tiers, because context matters:
 *  - OPENERS stand alone as a first message.
 *  - FOLLOW_UPS presuppose prior results ("save THESE companies") — they only
 *    make sense mid-conversation and never appear on the hero or a first-turn
 *    typeahead.
 */
export const PROMPT_OPENERS = [
  "Find companies in fintech with $50M+ funding",
  "Find companies using Salesforce and HubSpot",
  "Find companies founded after 2020 in the US",
  "Find decision makers at companies in my account list",
  "Find VPs of sales at SaaS companies",
  "Find engineering leaders who are hiring",
  "Find people who joined their company in the last 6 months",
  "Find CXOs at B2B software companies in New York",
  "Find marketing leaders at companies with 200+ employees",
  "Find people with 10+ years of experience in cybersecurity",
  "Find prospects for my product",
  "Build a list of SaaS companies that raised a Series B",
  "Build a prospect list of RevOps leaders",
  "Create a sequence to book meetings with new leads",
  "Create a follow-up sequence with email and LinkedIn steps",
  "Show my sequences and their reply rates",
  "Show my connected inboxes",
  "Show my seller profiles",
  "Export a contact list to Salesforce",
  "Check my inbox for replies this week",
  "Who opened my emails but didn't reply?",
  "How many credits do I have left?"
];

export const PROMPT_FOLLOW_UPS = [
  "Save these companies to an account list",
  "Add these people to a contact list",
  "Exclude people already in my contact list",
  "Export these contacts to Salesforce",
  "Find decision makers at these companies",
  "Create a sequence for these prospects"
];

/**
 * Rank entries against the typed text: every typed word must prefix-match a
 * word in the prompt; prompts starting with the input rank first. Follow-up
 * prompts are only eligible once the conversation has context.
 */
export const suggestPrompts = (input, hasContext = false, max = 6) => {
  const text = (input || "").trim().toLowerCase();
  if (text.length < 2) return [];
  const bank = hasContext
    ? [...PROMPT_FOLLOW_UPS, ...PROMPT_OPENERS]
    : PROMPT_OPENERS;
  const words = text.split(/\s+/);
  return bank
    .map(prompt => {
      const lower = prompt.toLowerCase();
      const promptWords = lower.split(/\s+/);
      const allMatch = words.every(word =>
        promptWords.some(pw => pw.startsWith(word))
      );
      if (!allMatch) return null;
      return { prompt, score: lower.startsWith(text) ? 0 : 1 };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .slice(0, max)
    .map(entry => entry.prompt);
};
